/**
 * Timezone utilities for the meetings calendar.
 *
 * The HTML `datetime-local` input always assumes the BROWSER's local time. To
 * support an arbitrary IANA timezone we treat the input's value as if it were
 * "wall-clock time in the picked TZ" and convert in both directions using the
 * `formatToParts` trick (works in every modern browser).
 */

// All IANA zones the browser knows about (~400+). Falls back to a curated list
// on the rare browser without Intl.supportedValuesOf (e.g. older Safari).
export function listIanaTimezones() {
  try {
    if (typeof Intl?.supportedValuesOf === "function") {
      return Intl.supportedValuesOf("timeZone");
    }
  } catch { /* fall through */ }
  return TIMEZONE_FALLBACK;
}

export function detectBrowserTimezone() {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"; }
  catch { return "UTC"; }
}

/** Returns "+05:30", "-04:00", "Z" for a UTC date as observed in `tz`. */
export function offsetLabelForTZ(date, tz) {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: tz, timeZoneName: "shortOffset",
    }).formatToParts(date);
    const tzPart = parts.find(p => p.type === "timeZoneName")?.value || "";
    // Examples: "GMT+5:30", "GMT-4", "UTC"
    const m = tzPart.match(/GMT([+-])(\d{1,2})(?::(\d{2}))?/);
    if (!m) return "UTC";
    const sign = m[1];
    const hh = String(parseInt(m[2], 10)).padStart(2, "0");
    const mm = (m[3] || "00").padStart(2, "0");
    return `${sign}${hh}:${mm}`;
  } catch { return ""; }
}

/** Format a UTC ISO string for display in `tz` — "Mon, Jan 6, 9:30 AM". */
export function fmtInTZ(iso, tz, opts = {}) {
  try {
    const d = new Date(iso);
    return new Intl.DateTimeFormat([], {
      timeZone: tz,
      weekday: opts.weekday ?? "short",
      month: opts.month ?? "short",
      day: opts.day ?? "numeric",
      hour: opts.hour ?? "2-digit",
      minute: opts.minute ?? "2-digit",
      hour12: opts.hour12 ?? true,
    }).format(d);
  } catch { return ""; }
}

/** Hour:minute only, e.g. "09:30 AM" rendered in `tz`. */
export function fmtTimeInTZ(iso, tz) {
  try {
    return new Intl.DateTimeFormat([], {
      timeZone: tz, hour: "2-digit", minute: "2-digit", hour12: true,
    }).format(new Date(iso));
  } catch { return ""; }
}

/** Y/M/D in `tz` — used for grouping events into calendar cells. */
export function ymdInTZ(iso, tz) {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit",
    }).formatToParts(new Date(iso));
    const get = (t) => parts.find(p => p.type === t)?.value;
    return `${get("year")}-${get("month")}-${get("day")}`;
  } catch { return ""; }
}

/**
 * Given a `datetime-local` string ("YYYY-MM-DDTHH:mm"), treat it as wall-clock
 * time in `tz` and return the corresponding UTC ISO string.
 *
 * Algorithm: build a tentative UTC date from the parts, measure how far off it
 * is when re-rendered in `tz`, and correct by that delta. This is more reliable
 * than naïve offset math (handles DST correctly).
 */
export function localStrInTZToUTC(localStr, tz) {
  if (!localStr) return null;
  const [date, time = "00:00"] = localStr.split("T");
  const [y, m, d] = date.split("-").map(Number);
  const [hh, mm] = time.split(":").map(Number);
  // Tentative UTC date assuming the wall-clock IS UTC
  let utc = Date.UTC(y, m - 1, d, hh, mm);
  // Two passes are enough — handles DST transitions cleanly. We include month +
  // year deltas in the correction so month/year boundary crossings (e.g. Jan 1
  // at 00:30 in Asia/Tokyo measured from UTC) converge in a single pass.
  const MS_DAY = 24 * 60 * 60 * 1000;
  for (let i = 0; i < 2; i++) {
    const rendered = getWallClockInTZ(new Date(utc), tz);
    // Build an absolute "instant" represented by the rendered wall-clock and
    // subtract it from the desired instant (in calendar days + minutes).
    const renderedAsUTC = Date.UTC(rendered.y, rendered.mo - 1, rendered.d, rendered.h, rendered.m);
    const desiredAsUTC = Date.UTC(y, m - 1, d, hh, mm);
    const delta = renderedAsUTC - desiredAsUTC;
    if (delta === 0) break;
    utc -= delta;
    // Guard: bail out if we've wandered absurdly far (would indicate Intl returned bogus parts)
    if (Math.abs(utc - Date.UTC(y, m - 1, d, hh, mm)) > 365 * MS_DAY) break;
  }
  return new Date(utc).toISOString();
}

/** Render a UTC date as wall-clock parts in `tz`. */
function getWallClockInTZ(date, tz) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", hour12: false,
  }).formatToParts(date);
  const get = (t) => parseInt(parts.find(p => p.type === t)?.value, 10);
  let h = get("hour");
  if (h === 24) h = 0; // some platforms render midnight as 24
  return { y: get("year"), mo: get("month"), d: get("day"), h, m: get("minute") };
}

/**
 * Inverse of localStrInTZToUTC: render a UTC ISO as a "YYYY-MM-DDTHH:mm" string
 * suitable for a `datetime-local` input, expressed in `tz`.
 */
export function utcToLocalStrInTZ(utcIso, tz) {
  if (!utcIso) return "";
  const wc = getWallClockInTZ(new Date(utcIso), tz);
  const pad = (n) => String(n).padStart(2, "0");
  return `${wc.y}-${pad(wc.mo)}-${pad(wc.d)}T${pad(wc.h)}:${pad(wc.m)}`;
}

/** Small curated fallback for browsers without supportedValuesOf. */
const TIMEZONE_FALLBACK = [
  "UTC",
  "America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles",
  "America/Anchorage", "America/Honolulu", "America/Toronto", "America/Mexico_City",
  "America/Sao_Paulo", "America/Buenos_Aires", "America/Bogota", "America/Lima",
  "Europe/London", "Europe/Dublin", "Europe/Paris", "Europe/Berlin", "Europe/Madrid",
  "Europe/Rome", "Europe/Amsterdam", "Europe/Stockholm", "Europe/Moscow", "Europe/Istanbul",
  "Africa/Cairo", "Africa/Johannesburg", "Africa/Lagos", "Africa/Nairobi",
  "Asia/Dubai", "Asia/Karachi", "Asia/Kolkata", "Asia/Dhaka", "Asia/Bangkok",
  "Asia/Jakarta", "Asia/Singapore", "Asia/Hong_Kong", "Asia/Shanghai", "Asia/Tokyo",
  "Asia/Seoul", "Asia/Manila", "Asia/Taipei",
  "Australia/Perth", "Australia/Sydney", "Australia/Melbourne", "Australia/Brisbane",
  "Pacific/Auckland", "Pacific/Fiji", "Pacific/Honolulu",
];
